export const MUSIC_MAX_TRACKS = 100;
export const MUSIC_MAX_PLAYERS = 80;
export const MUSIC_PRESENCE_STALE_MS = 30_000;
export type MusicPresence = { visibility: "visible" | "hidden"; updatedAt: number; changedAt: number };
export type MusicRosterEntry = { name: string; rated: number; currentRated: boolean; presence: "here" | "away" | "offline" | "unknown"; since: number | null };
export const MUSIC_CODE = /^M[A-Z2-9]{7}$/;
export type MusicTrack = { videoId: string; title: string; artist: string };
export type MusicPlayer = { tokenHash: string; name: string; votes: Record<string, number> };
export type MusicLyrics = { videoId: string; trackName: string; artistName: string; plainLyrics: string; syncedLyrics: string; source?: "video" | "lrclib" };
export type MusicMeaning = { videoId: string; term: string; line: string; translation: string };
export type MusicPlayback = { videoId: string; seconds: number; playing: boolean; rate: number; offset: number; updatedAt: number };
export type MusicRoom = {
    code: string; owner: string; className: string; schoolYear: string; createdAt: number;
    queue: MusicTrack[]; current: number; opened: string[]; revealed: boolean; finished: boolean; preparing?: boolean;
    revision: number; players: MusicPlayer[]; lyrics?: MusicLyrics | null; meaning?: MusicMeaning | null; playback?: MusicPlayback | null;
};
export type MusicSummary = { videoId: string; title: string; artist: string; count: number; average: number | null; distribution: number[] };
export type MusicView = {
    code: string; className: string; schoolYear: string; createdAt: number; queue: MusicTrack[];
    current: number; opened: string[]; revealed: boolean; finished: boolean; revision: number; preparing?: boolean;
    participantCount: number; results: MusicSummary[]; votes: Record<string, number>;
    roster?: MusicRosterEntry[]; lyrics?: MusicLyrics | null; meaning?: MusicMeaning | null; playback?: MusicPlayback | null; serverTime?: number;
};
export type MusicSessionRef = { code: string; className: string; schoolYear: string; createdAt: number };
export type MusicLibrary = { accountId: string; revision: number; tracks: MusicTrack[]; sessions: MusicSessionRef[] };

/** Scan known YouTube hosts rather than splitting on delimiters: prose, CSV, bullets,
 * Markdown, HTML and directly adjacent URLs all keep their original encounter order. */
export function parseYouTubeLinks(input: string): { tracks: MusicTrack[]; duplicates: number; invalid: number; overflow: number } {
    const text = input.replace(/&amp;/gi, "&");
    const starts = [...text.matchAll(/(?:(?:https?:)?\/\/)?(?:(?:www|m|music)\.)?(?:youtube\.com|youtube-nocookie\.com|youtu\.be)\//gi)]
        .filter(m => /^https?:\/\//i.test(m[0]) || m.index === 0 || !/[\w.@/-]/.test(text[m.index! - 1]));
    const ids = new Set<string>(); const tracks: MusicTrack[] = [];
    let invalid = 0, duplicates = 0, overflow = 0;
    starts.forEach((match, index) => {
        let candidate = text.slice(match.index, starts[index + 1]?.index).split(/[\s<>"'`，,;|)\]}]/)[0];
        if (candidate.startsWith("//")) candidate = `https:${candidate}`;
        else if (!/^https?:\/\//i.test(candidate)) candidate = `https://${candidate}`;
        let id = "";
        try {
            const url = new URL(candidate);
            const host = url.hostname.toLowerCase().replace(/^(www|m|music)\./, "");
            if (host === "youtu.be") id = url.pathname.split("/")[1] ?? "";
            else if (host === "youtube.com" || host === "youtube-nocookie.com") {
                id = url.pathname === "/watch" ? url.searchParams.get("v") ?? "" : /^\/(embed|shorts|live|v)\//.test(url.pathname) ? url.pathname.split("/")[2] ?? "" : "";
            }
            id = id.replace(/[.!?:]+$/, "");
        } catch { /* Invalid fragments are reported with the import preview. */ }
        if (!/^[a-zA-Z0-9_-]{11}$/.test(id)) { invalid++; return; }
        if (ids.has(id)) { duplicates++; return; }
        ids.add(id);
        if (tracks.length >= MUSIC_MAX_TRACKS) { overflow++; return; }
        tracks.push({ videoId: id, title: id, artist: "" });
    });
    return { tracks, duplicates, invalid, overflow };
}

export function validateTracks(input: unknown): MusicTrack[] {
    if (!Array.isArray(input) || !input.length || input.length > MUSIC_MAX_TRACKS) throw new Error("musicInvalidPlaylist");
    const seen = new Set<string>();
    return input.map(value => {
        if (!value || typeof value !== "object" || !/^[\w-]{11}$/.test(value.videoId) || seen.has(value.videoId)) throw new Error("musicInvalidPlaylist");
        seen.add(value.videoId);
        return { videoId: value.videoId, title: String(value.title || value.videoId).slice(0, 200), artist: String(value.artist || "").slice(0, 120) };
    });
}

export function createMusicRoom(code: string, owner: string, data: Record<string, unknown>): MusicRoom {
    const className = typeof data.className === "string" ? data.className.trim().slice(0, 40) : "";
    const schoolYear = typeof data.schoolYear === "string" ? data.schoolYear.trim() : "";
    if (!MUSIC_CODE.test(code) || !className || !/^20\d{2}$/.test(schoolYear)) throw new Error("musicClassRequired");
    return { code, owner, className, schoolYear, queue: validateTracks(data.tracks), current: 0, createdAt: Date.now(), opened: [], revealed: false, finished: false, revision: 1, players: [] };
}
export function createMusicLobby(code: string, owner: string): MusicRoom {
    if (!MUSIC_CODE.test(code)) throw new Error("musicInvalidAction");
    return { code, owner, className: "Lyric Learn", schoolYear: String(new Date().getFullYear()), queue: [], current: 0,
        createdAt: Date.now(), opened: [], revealed: false, finished: false, preparing: true, revision: 1, players: [] };
}

export function musicSummaries(room: MusicRoom): MusicSummary[] {
    return room.queue.map(track => {
        const distribution = [0, 0, 0, 0, 0]; let total = 0, count = 0;
        for (const player of room.players) { const vote = player.votes[track.videoId]; if (vote >= 1 && vote <= 5) { distribution[vote - 1]++; count++; total += vote; } }
        return { ...track, count, average: count ? total / count : null, distribution };
    });
}

export function musicView(room: MusicRoom, player?: MusicPlayer, host = false, presence: ReadonlyMap<string, MusicPresence> = new Map(), now = Date.now()): MusicView {
    const queue = host ? room.queue : room.queue.filter((track, index) => index === room.current || room.opened.includes(track.videoId));
    return { code: room.code, className: room.className, schoolYear: room.schoolYear, createdAt: room.createdAt,
        queue, current: host ? room.current : queue.findIndex(track => track.videoId === room.queue[room.current]?.videoId),
        opened: room.opened, revealed: room.revealed, finished: room.finished, revision: room.revision, preparing: room.preparing ?? false,
        lyrics: !room.finished && room.lyrics && room.lyrics.videoId === room.queue[room.current]?.videoId ? room.lyrics : null,
        meaning: !room.finished && room.lyrics && room.lyrics.videoId === room.queue[room.current]?.videoId ? room.meaning ?? null : null,
        playback: !room.finished && room.lyrics && room.lyrics.videoId === room.queue[room.current]?.videoId ? room.playback ?? null : null, serverTime: now,
        participantCount: room.players.length, results: host ? musicSummaries(room) : room.revealed ? musicSummaries(room).filter(summary => room.opened.includes(summary.videoId)) : [], votes: player?.votes ?? {},
        ...(host ? { roster: room.players.map(p => {
            const sample = presence.get(p.tokenHash);
            const state = !sample ? "unknown" : now - sample.updatedAt >= MUSIC_PRESENCE_STALE_MS ? "offline" : sample.visibility === "hidden" ? "away" : "here";
            return { name: p.name, rated: Object.keys(p.votes).length, currentRated: Boolean(p.votes[room.queue[room.current]?.videoId ?? ""]),
                presence: state, since: sample ? state === "offline" ? sample.updatedAt : sample.changedAt : null };
        }) } : {}) };
}

export function commandMusicRoom(room: MusicRoom, command: Record<string, unknown>): void {
    if (room.finished) throw new Error("musicFinished");
    const action = command.action;
    if (room.preparing) {
        if (action !== "begin") throw new Error("musicVotingClosed");
        const started = createMusicRoom(room.code, room.owner, command);
        Object.assign(room, { className: started.className, schoolYear: started.schoolYear, queue: started.queue, createdAt: started.createdAt, preparing: false });
        room.revision++; return;
    }
    if (action === "play") {
        const index = Number(command.index);
        if (!Number.isInteger(index) || index < 0 || index >= room.queue.length) throw new Error("musicInvalidSong");
        if (room.current !== index) { room.lyrics = null; room.meaning = null; room.playback = null; room.revealed = false; }
        room.current = index;
    } else if (action === "lyrics") {
        const lyrics = command.lyrics as Partial<MusicLyrics> | null;
        if (lyrics === null) { room.lyrics = null; room.meaning = null; room.playback = null; }
        else {
            // Only the host's selected match for the current song can reach student screens.
            if (!lyrics || lyrics.videoId !== room.queue[room.current].videoId ||
                typeof lyrics.trackName !== "string" || typeof lyrics.artistName !== "string" ||
                typeof lyrics.plainLyrics !== "string" || typeof lyrics.syncedLyrics !== "string" ||
                lyrics.trackName.length > 200 || lyrics.artistName.length > 120 ||
                lyrics.plainLyrics.length + lyrics.syncedLyrics.length > 140_000) throw new Error("musicInvalidSong");
            room.meaning = null;
            room.playback = null;
            room.lyrics = { videoId: lyrics.videoId, trackName: lyrics.trackName, artistName: lyrics.artistName,
                plainLyrics: lyrics.plainLyrics, syncedLyrics: lyrics.syncedLyrics, ...(lyrics.source === "video" ? { source: "video" as const } : {}) };
        }
    } else if (action === "playback") {
        const playback = command.playback as Partial<MusicPlayback>;
        if (!playback || !room.lyrics || playback.videoId !== room.queue[room.current].videoId ||
            typeof playback.seconds !== "number" || !Number.isFinite(playback.seconds) || playback.seconds < 0 || playback.seconds > 86400 ||
            typeof playback.playing !== "boolean" || typeof playback.rate !== "number" || !Number.isFinite(playback.rate) || playback.rate < 0.25 || playback.rate > 2 ||
            typeof playback.offset !== "number" || !Number.isFinite(playback.offset) || Math.abs(playback.offset) > 300) throw new Error("musicInvalidSong");
        room.playback = { videoId: playback.videoId, seconds: playback.seconds, playing: playback.playing, rate: playback.rate, offset: playback.offset, updatedAt: Date.now() };
    } else if (action === "meaning") {
        const meaning = command.meaning as Partial<MusicMeaning>;
        if (!meaning || !room.lyrics || meaning.videoId !== room.queue[room.current].videoId ||
            typeof meaning.term !== "string" || !meaning.term.trim() || meaning.term.length > 160 ||
            typeof meaning.line !== "string" || meaning.line.length > 1000 ||
            typeof meaning.translation !== "string" || !meaning.translation.trim() || meaning.translation.length > 4000 ||
            !meaning.line.includes(meaning.term) || !parseLyricLines(room.lyrics.plainLyrics, room.lyrics.syncedLyrics).some(line => line.text === meaning.line)) throw new Error("musicSelectTerm");
        room.meaning = { videoId: meaning.videoId, term: meaning.term, line: meaning.line, translation: meaning.translation };
    } else if (action === "open") {
        const id = room.queue[room.current].videoId;
        if (!room.opened.includes(id)) room.opened.push(id);
    } else if (action === "add") {
        const tracks = validateTracks(command.tracks).filter(t => !room.queue.some(q => q.videoId === t.videoId));
        if (!tracks.length) throw new Error("musicAlreadyQueued");
        if (room.queue.length + tracks.length > MUSIC_MAX_TRACKS) throw new Error("musicPlaylistFull");
        room.queue.splice(command.position === "next" ? room.current + 1 : room.queue.length, 0, ...tracks);
    } else if (action === "reveal") room.revealed = !room.revealed;
    else if (action === "finish") { room.finished = true; room.revealed = true; }
    else throw new Error("musicInvalidAction");
    room.revision++;
}

export function rateMusic(room: MusicRoom, player: MusicPlayer, videoId: unknown, rating: unknown): void {
    if (room.finished) throw new Error("musicFinished");
    if (typeof videoId !== "string" || !room.opened.includes(videoId)) throw new Error("musicVotingClosed");
    if (typeof rating !== "number" || !Number.isInteger(rating) || rating < 1 || rating > 5) throw new Error("musicInvalidRating");
    player.votes[videoId] = rating; room.revision++;
}

export type LyricLine = { text: string; seconds: number | null };
/** A sentence stays active until the next timestamp, including after a backward seek. */
export function lyricIndexAt(lines: LyricLine[], videoSeconds: number, offset = 0): number {
    let active = -1;
    for (let i = 0; i < lines.length; i++) if (lines[i].seconds !== null && lines[i].seconds! + offset <= videoSeconds) active = i;
    return active;
}
export function parseLyricLines(plain: string, synced = ""): LyricLine[] {
    const timed: LyricLine[] = [];
    for (const line of synced.split("\n")) {
        const stamps = [...line.matchAll(/\[(\d+):(\d+(?:\.\d+)?)\]/g)];
        const text = line.replace(/\[[^\]]*\]/g, "").trim();
        if (text) stamps.forEach(m => timed.push({ text, seconds: Number(m[1]) * 60 + Number(m[2]) }));
    }
    return timed.length ? timed.sort((a, b) => a.seconds! - b.seconds!) : plain.split("\n").map(text => ({ text, seconds: null }));
}

export function guessMusicMetadata(title: string, author: string): { title: string; artist: string } {
    const clean = title.replace(/\s*[\[(](?:official|lyrics?|music video|audio|visuali[sz]er|hd|4k)[^\])]*[\])]/gi, "").replace(/\s*\|.*$/, "").trim();
    const parts = clean.split(/\s+[-–—]\s+/);
    return parts.length >= 2 ? { artist: parts[0].slice(0, 120), title: parts.slice(1).join(" - ").slice(0, 200) }
        : { title: clean.slice(0, 200), artist: author.replace(/(?:VEVO|\s*-\s*Topic)$/i, "").trim().slice(0, 120) };
}
